import { useState, useEffect } from "react";
import { useAuth, api } from "../App";
import { toast } from "@/lib/toast";
import { formatMoney } from "@/lib/currency";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CurrencyInput } from "../components/ui/currency-input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { 
  Plus, Users, DollarSign, Trash2, ChevronRight, ArrowRight, 
  Lock, Unlock, UserPlus, Receipt, Calculator, Share2, Copy, Check,
  Edit2, Clock, Calendar, AlertTriangle
} from "lucide-react";

const SharedExpensesPage = () => {
  const { user } = useAuth();
  const userCurrency = user?.currency || "COP";
  
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupDetails, setGroupDetails] = useState(null);
  const [members, setMembers] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  
  // Dialogs
  const [createGroupOpen, setCreateGroupOpen] = useState(false);
  const [joinGroupOpen, setJoinGroupOpen] = useState(false);
  const [addMemberOpen, setAddMemberOpen] = useState(false);
  const [addExpenseOpen, setAddExpenseOpen] = useState(false);
  const [editExpenseOpen, setEditExpenseOpen] = useState(false);
  const [deleteGroupOpen, setDeleteGroupOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copiedId, setCopiedId] = useState(false);
  
  // Forms
  const [groupForm, setGroupForm] = useState({ name: "", description: "" });
  const [joinGroupId, setJoinGroupId] = useState("");
  const [joinGroupName, setJoinGroupName] = useState("");
  const [memberForm, setMemberForm] = useState({ name: "" });
  const [expenseForm, setExpenseForm] = useState({ amount: "", description: "", paid_by_member_id: "" });
  const [editingExpense, setEditingExpense] = useState(null);
  const [editExpenseForm, setEditExpenseForm] = useState({ amount: "", description: "", paid_by_member_id: "" });
  
  // Delete group OTP flow
  const [deleteOtpStep, setDeleteOtpStep] = useState("request"); // "request" | "verify"
  const [deleteOtp, setDeleteOtp] = useState("");
  const [deleteGroupName, setDeleteGroupName] = useState("");

  const formatCurrency = (amount) => {
    return formatMoney(amount, userCurrency, { absolute: true, style: "decimal" });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-CO', { 
      day: 'numeric', 
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fetch groups
  const fetchGroups = async () => {
    try {
      const response = await api.get("/shared-groups");
      setGroups(response.data);
    } catch (error) {
      toast.error("Error al cargar grupos");
    } finally {
      setLoading(false);
    }
  };

  // Fetch group details
  const fetchGroupDetails = async (groupId) => {
    try {
      const [groupRes, membersRes, expensesRes, summaryRes] = await Promise.all([
        api.get(`/shared-groups/${groupId}`),
        api.get(`/shared-groups/${groupId}/members`),
        api.get(`/shared-groups/${groupId}/expenses`),
        api.get(`/shared-groups/${groupId}/summary`)
      ]);
      
      setGroupDetails(groupRes.data);
      setMembers(membersRes.data);
      setExpenses(expensesRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error("Error al cargar detalles del grupo");
    }
  };

  useEffect(() => {
    fetchGroups();
  }, []);

  useEffect(() => {
    if (selectedGroup) {
      fetchGroupDetails(selectedGroup);
    }
  }, [selectedGroup]);

  // Create group
  const handleCreateGroup = async (e) => {
    e.preventDefault();
    if (!groupForm.name) {
      toast.error("El nombre es requerido");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/shared-groups", groupForm);
      toast.success("Grupo creado");
      setCreateGroupOpen(false);
      setGroupForm({ name: "", description: "" });
      fetchGroups();
    } catch (error) {
      toast.error("Error al crear grupo");
    } finally {
      setSubmitting(false);
    }
  };

  // Join group
  const handleJoinGroup = async (e) => {
    e.preventDefault();
    if (!joinGroupId.trim()) {
      toast.error("El ID del grupo es requerido");
      return;
    }
    if (!joinGroupName.trim() || joinGroupName.trim().length < 2) {
      toast.error("Tu nombre es requerido (mínimo 2 caracteres)");
      return;
    }
    setSubmitting(true);
    try {
      const response = await api.post(`/shared-groups/join/${joinGroupId.trim()}`, {
        name: joinGroupName.trim()
      });
      toast.success(response.data.message || "Te uniste al grupo");
      setJoinGroupOpen(false);
      setJoinGroupId("");
      setJoinGroupName("");
      fetchGroups();
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail === "Grupo no encontrado") {
        toast.error("No se encontró un grupo con ese ID");
      } else if (detail === "Ya eres miembro de este grupo") {
        toast.error("Ya eres miembro de este grupo");
      } else if (detail === "No puedes unirte a un grupo cerrado") {
        toast.error("Este grupo está cerrado");
      } else {
        toast.error(detail || "Error al unirse al grupo");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Add member
  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!memberForm.name) {
      toast.error("El nombre es requerido");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/shared-groups/${selectedGroup}/members`, memberForm);
      toast.success("Miembro agregado");
      setAddMemberOpen(false);
      setMemberForm({ name: "" });
      fetchGroupDetails(selectedGroup);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar miembro");
    } finally {
      setSubmitting(false);
    }
  };

  // Add expense
  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseForm.amount || !expenseForm.description || !expenseForm.paid_by_member_id) {
      toast.error("Completa todos los campos");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/shared-groups/${selectedGroup}/expenses`, {
        ...expenseForm,
        amount: parseFloat(expenseForm.amount),
        currency: userCurrency
      });
      toast.success("Gasto registrado");
      setAddExpenseOpen(false);
      setExpenseForm({ amount: "", description: "", paid_by_member_id: "" });
      fetchGroupDetails(selectedGroup);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al agregar gasto");
    } finally {
      setSubmitting(false);
    }
  };

  // Edit expense
  const openEditExpense = (expense) => {
    setEditingExpense(expense);
    setEditExpenseForm({
      amount: String(expense.amount),
      description: expense.description,
      paid_by_member_id: expense.paid_by_member_id
    });
    setEditExpenseOpen(true);
  };

  const handleEditExpense = async (e) => {
    e.preventDefault();
    if (!editExpenseForm.amount || !editExpenseForm.description || !editExpenseForm.paid_by_member_id) {
      toast.error("Completa todos los campos");
      return;
    }
    setSubmitting(true);
    try {
      await api.put(`/shared-groups/${selectedGroup}/expenses/${editingExpense.id}`, {
        amount: parseFloat(editExpenseForm.amount),
        description: editExpenseForm.description,
        paid_by_member_id: editExpenseForm.paid_by_member_id
      });
      toast.success("Gasto actualizado");
      setEditExpenseOpen(false);
      setEditingExpense(null);
      fetchGroupDetails(selectedGroup);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar gasto");
    } finally {
      setSubmitting(false);
    }
  };

  // Delete expense
  const handleDeleteExpense = async (expenseId) => {
    if (!confirm("¿Eliminar este gasto?")) return;
    try {
      await api.delete(`/shared-groups/${selectedGroup}/expenses/${expenseId}`);
      toast.success("Gasto eliminado");
      fetchGroupDetails(selectedGroup);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  // Delete member
  const handleDeleteMember = async (memberId) => {
    if (!confirm("¿Eliminar este miembro?")) return;
    try {
      await api.delete(`/shared-groups/${selectedGroup}/members/${memberId}`);
      toast.success("Miembro eliminado");
      fetchGroupDetails(selectedGroup);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    }
  };

  // Close group
  const handleCloseGroup = async () => {
    if (!confirm("¿Cerrar el grupo? Se registrarán los gastos en las finanzas personales de cada miembro registrado.")) return;
    try {
      const response = await api.post(`/shared-groups/${selectedGroup}/close`);
      toast.success(`Grupo cerrado. ${response.data.transactions_created} gastos registrados.`);
      fetchGroupDetails(selectedGroup);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al cerrar grupo");
    }
  };

  // Reopen group
  const handleReopenGroup = async () => {
    if (!confirm("¿Reabrir el grupo? Se eliminarán los gastos registrados en las finanzas personales.")) return;
    try {
      await api.post(`/shared-groups/${selectedGroup}/reopen`);
      toast.success("Grupo reabierto");
      fetchGroupDetails(selectedGroup);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al reabrir grupo");
    }
  };

  // Delete group with OTP
  const openDeleteGroupDialog = () => {
    setDeleteOtpStep("request");
    setDeleteOtp("");
    setDeleteGroupOpen(true);
  };

  const requestDeleteOtp = async () => {
    setSubmitting(true);
    try {
      const response = await api.post(`/shared-groups/${selectedGroup}/request-delete-otp`);
      setDeleteGroupName(response.data.group_name);
      setDeleteOtpStep("verify");
      if (response.data.whatsapp_sent) {
        toast.success("Código enviado a tu WhatsApp");
      } else if (response.data.otp_for_testing) {
        // For testing - show OTP in toast
        toast.info(`Código de prueba: ${response.data.otp_for_testing}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al solicitar código");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDeleteGroup = async () => {
    if (!deleteOtp || deleteOtp.length !== 6) {
      toast.error("Ingresa el código de 6 dígitos");
      return;
    }
    setSubmitting(true);
    try {
      await api.post(`/shared-groups/${selectedGroup}/confirm-delete`, { otp: deleteOtp });
      toast.success("Grupo eliminado");
      setDeleteGroupOpen(false);
      setSelectedGroup(null);
      setGroupDetails(null);
      fetchGroups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar grupo");
    } finally {
      setSubmitting(false);
    }
  };

  // Copy group ID
  const copyGroupId = () => {
    navigator.clipboard.writeText(selectedGroup);
    setCopiedId(true);
    setTimeout(() => setCopiedId(false), 2000);
  };

  const isOwner = groupDetails?.owner_id === user?.id;
  const isOpen = groupDetails?.status === "open";

  // Groups list view
  if (!selectedGroup) {
    return (
      <div className="p-4 md:p-6 max-w-6xl mx-auto">
        {/* Header - 2 rows on mobile */}
        <div className="mb-6">
          {/* Row 1: Title and description */}
          <div className="mb-3">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white font-['Outfit']">
              Compras Compartidas
            </h1>
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              Divide gastos con amigos en viajes, fiestas y más
            </p>
          </div>
          
          {/* Row 2: Buttons */}
          <div className="flex gap-2">
            {/* Join Group Button */}
            <Dialog open={joinGroupOpen} onOpenChange={setJoinGroupOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="flex-1 sm:flex-none">
                  <UserPlus className="w-4 h-4 mr-1 sm:mr-2" />
                  <span>Unirse</span>
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Unirse a un Grupo</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleJoinGroup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="group-id">ID del grupo *</Label>
                    <Input
                      id="group-id"
                      placeholder="Pega aquí el ID que te compartieron"
                      value={joinGroupId}
                      onChange={(e) => setJoinGroupId(e.target.value)}
                    />
                    <p className="text-xs text-slate-500">
                      Pide al creador del grupo que te comparta el ID de invitación
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="join-name">Tu nombre *</Label>
                    <Input
                      id="join-name"
                      placeholder="¿Cómo te llamas?"
                      value={joinGroupName}
                      onChange={(e) => setJoinGroupName(e.target.value)}
                    />
                    <p className="text-xs text-slate-500">
                      Este nombre verán los demás miembros del grupo
                    </p>
                  </div>
                  <Button type="submit" className="w-full" disabled={submitting}>
                    {submitting ? "Uniéndose..." : "Unirme al Grupo"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>

            {/* Create Group Button */}
            <Dialog open={createGroupOpen} onOpenChange={setCreateGroupOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary flex-1 sm:flex-none" size="sm">
                  <Plus className="w-4 h-4 mr-1 sm:mr-2" />
                  <span>Nuevo Grupo</span>
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Crear Grupo</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateGroup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre del grupo *</Label>
                  <Input
                    id="name"
                    placeholder="Ej: Viaje a Cartagena"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({...groupForm, name: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="description">Descripción (opcional)</Label>
                  <Input
                    id="description"
                    placeholder="Gastos del viaje con amigos"
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({...groupForm, description: e.target.value})}
                  />
                </div>
                <Button type="submit" className="w-full btn-primary" disabled={submitting}>
                  {submitting ? "Creando..." : "Crear Grupo"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12 text-slate-500">Cargando...</div>
        ) : groups.length === 0 ? (
          <Card className="text-center py-12">
            <CardContent>
              <Share2 className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-slate-700 dark:text-slate-200 mb-2">
                No tienes grupos
              </h3>
              <p className="text-slate-500 dark:text-slate-400 mb-4">
                Crea tu primer grupo para dividir gastos con amigos
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {groups.map((group) => (
              <Card 
                key={group.id} 
                className="cursor-pointer hover:shadow-lg transition-shadow border-slate-200 dark:border-slate-700"
                onClick={() => setSelectedGroup(group.id)}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <h3 className="font-bold text-slate-900 dark:text-white">{group.name}</h3>
                      {group.description && (
                        <p className="text-sm text-slate-500 dark:text-slate-400">{group.description}</p>
                      )}
                    </div>
                    <Badge variant={group.status === "open" ? "default" : "secondary"}>
                      {group.status === "open" ? (
                        <><Unlock className="w-3 h-3 mr-1" /> Abierto</>
                      ) : (
                        <><Lock className="w-3 h-3 mr-1" /> Cerrado</>
                      )}
                    </Badge>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-slate-500 dark:text-slate-400 flex items-center gap-1">
                      <Users className="w-4 h-4" /> {group.members_count} miembros
                    </span>
                    <span className="font-bold text-slate-900 dark:text-white">
                      ${formatCurrency(group.total_spent)}
                    </span>
                  </div>
                  
                  <div className="mt-3 flex items-center justify-end text-cyan-600">
                    <span className="text-sm">Ver detalles</span>
                    <ChevronRight className="w-4 h-4" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Group detail view
  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <Button variant="ghost" size="sm" onClick={() => { setSelectedGroup(null); setGroupDetails(null); }} className="self-start">
          ← Volver
        </Button>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white font-['Outfit'] truncate">
              {groupDetails?.name || "Cargando..."}
            </h1>
            <Badge variant={isOpen ? "default" : "secondary"} className="shrink-0">
              {isOpen ? "Abierto" : "Cerrado"}
            </Badge>
          </div>
          {groupDetails?.description && (
            <p className="text-slate-500 dark:text-slate-400 text-sm truncate">{groupDetails.description}</p>
          )}
        </div>
        
        <div className="flex gap-2 self-start sm:self-auto">
          {isOwner && (
            <>
              {isOpen ? (
                <Button variant="outline" size="sm" onClick={handleCloseGroup} className="text-orange-600 border-orange-300">
                  <Lock className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Cerrar</span>
                </Button>
              ) : (
                <Button variant="outline" size="sm" onClick={handleReopenGroup} className="text-green-600 border-green-300">
                  <Unlock className="w-4 h-4 sm:mr-2" /> <span className="hidden sm:inline">Reabrir</span>
                </Button>
              )}
            </>
          )}
          {/* Delete button - available to all members with OTP confirmation */}
          <Button variant="destructive" size="icon" className="h-9 w-9" onClick={openDeleteGroupDialog}>
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <Card className="bg-gradient-to-br from-cyan-500 to-blue-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <p className="text-cyan-100 text-xs sm:text-sm">Total Gastado</p>
              <p className="text-lg sm:text-2xl font-bold truncate">${formatCurrency(summary.total_spent)}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-emerald-500 to-green-600 text-white">
            <CardContent className="p-3 sm:p-4">
              <p className="text-emerald-100 text-xs sm:text-sm">Por Persona</p>
              <p className="text-lg sm:text-2xl font-bold truncate">${formatCurrency(summary.per_person)}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-100 dark:bg-slate-800">
            <CardContent className="p-3 sm:p-4">
              <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm">Miembros</p>
              <p className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-white">{summary.members_count}</p>
            </CardContent>
          </Card>
          <Card className="bg-slate-100 dark:bg-slate-800">
            <CardContent className="p-3 sm:p-4">
              <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm">Gastos</p>
              <p className="text-lg sm:text-2xl font-bold text-slate-900 dark:text-white">{summary.expenses_count}</p>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid gap-4 lg:gap-6 lg:grid-cols-3 overflow-hidden">
        {/* Members */}
        <Card className="lg:col-span-1 overflow-hidden">
          <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 min-w-0">
              <Users className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-500 shrink-0" /> 
              <span>Miembros</span>
            </CardTitle>
            {isOpen && (
              <Dialog open={addMemberOpen} onOpenChange={setAddMemberOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" className="shrink-0">
                    <UserPlus className="w-4 h-4" />
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Agregar Miembro</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddMember} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="member-name">Nombre *</Label>
                      <Input
                        id="member-name"
                        placeholder="Nombre del participante"
                        value={memberForm.name}
                        onChange={(e) => setMemberForm({...memberForm, name: e.target.value})}
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={submitting}>
                      {submitting ? "Agregando..." : "Agregar"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent className="space-y-2">
            {members.map((member) => {
              const memberSummary = summary?.members_summary?.find(m => m.id === member.id);
              return (
                <div key={member.id} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded-lg group">
                  <div>
                    <p className="font-medium text-slate-900 dark:text-white">
                      {member.name}
                      {member.user_id === user?.id && <span className="text-xs text-cyan-500 ml-1">(tú)</span>}
                      {member.user_id === groupDetails?.owner_id && <span className="text-xs text-amber-500 ml-1">👑</span>}
                    </p>
                    {memberSummary && (
                      <p className="text-xs text-slate-500">
                        Pagó: ${formatCurrency(memberSummary.total_paid)}
                        {memberSummary.balance > 0 && <span className="text-green-500 ml-1">(+${formatCurrency(memberSummary.balance)})</span>}
                        {memberSummary.balance < 0 && <span className="text-red-500 ml-1">(-${formatCurrency(Math.abs(memberSummary.balance))})</span>}
                      </p>
                    )}
                  </div>
                  {isOwner && isOpen && member.user_id !== groupDetails?.owner_id && (
                    <Button 
                      size="icon" 
                      variant="ghost" 
                      className="opacity-0 group-hover:opacity-100 h-8 w-8 text-red-500"
                      onClick={() => handleDeleteMember(member.id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              );
            })}
            
            {/* Share ID */}
            <div className="mt-4 p-3 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg overflow-hidden">
              <p className="text-xs text-slate-600 dark:text-slate-300 mb-1">ID para invitar:</p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-white dark:bg-slate-800 px-2 py-1 rounded flex-1 truncate overflow-hidden min-w-0">
                  {selectedGroup}
                </code>
                <Button size="sm" variant="ghost" onClick={copyGroupId} className="shrink-0">
                  {copiedId ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Expenses / Historial de Gastos */}
        <Card className="lg:col-span-2 overflow-hidden">
          <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2 min-w-0">
              <Receipt className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-500 shrink-0" /> 
              <span>Historial de Gastos</span>
            </CardTitle>
            {isOpen && (
              <Dialog open={addExpenseOpen} onOpenChange={setAddExpenseOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="btn-primary shrink-0">
                    <Plus className="w-4 h-4" />
                    <span className="ml-1 hidden sm:inline">Gasto</span>
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Registrar Gasto</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddExpense} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="paid_by">¿Quién pagó? *</Label>
                      <Select
                        value={expenseForm.paid_by_member_id}
                        onValueChange={(v) => setExpenseForm({...expenseForm, paid_by_member_id: v})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecciona quién pagó" />
                        </SelectTrigger>
                        <SelectContent>
                          {members.map((m) => (
                            <SelectItem key={m.id} value={m.id}>
                              {m.name} {m.user_id === user?.id && "(tú)"}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expense-amount">Monto *</Label>
                      <CurrencyInput
                        id="expense-amount"
                        currency={userCurrency}
                        placeholder="0"
                        value={expenseForm.amount}
                        onChange={(e) => setExpenseForm({...expenseForm, amount: e.target.value})}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="expense-desc">Concepto *</Label>
                      <Input
                        id="expense-desc"
                        placeholder="Ej: Cervezas, Hospedaje, Almuerzo"
                        value={expenseForm.description}
                        onChange={(e) => setExpenseForm({...expenseForm, description: e.target.value})}
                      />
                    </div>
                    <Button type="submit" className="w-full btn-primary" disabled={submitting}>
                      {submitting ? "Guardando..." : "Guardar Gasto"}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </CardHeader>
          <CardContent className="overflow-hidden">
            {expenses.length === 0 ? (
              <p className="text-center text-slate-500 py-8">No hay gastos registrados</p>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto overflow-x-hidden">
                {expenses.map((expense) => (
                  <div key={expense.id} className="flex items-center justify-between p-2 sm:p-3 bg-slate-50 dark:bg-slate-800 rounded-lg group gap-2">
                    <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                      <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center shrink-0">
                        <DollarSign className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-600" />
                      </div>
                      <div className="min-w-0">
                        <p className="font-medium text-slate-900 dark:text-white text-sm truncate">{expense.description}</p>
                        <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                          <span className="truncate">{expense.paid_by_name} pagó</span>
                          {expense.created_at && (
                            <span className="flex items-center gap-1 shrink-0">
                              <Clock className="w-3 h-3" />
                              {formatDate(expense.created_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 sm:gap-2 shrink-0">
                      <p className="font-bold text-slate-900 dark:text-white text-sm whitespace-nowrap">
                        ${formatCurrency(expense.amount)}
                      </p>
                      {isOpen && (expense.created_by_user_id === user?.id || isOwner) && (
                        <>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="opacity-0 group-hover:opacity-100 h-7 w-7 sm:h-8 sm:w-8 text-blue-500"
                            onClick={() => openEditExpense(expense)}
                          >
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button 
                            size="icon" 
                            variant="ghost" 
                            className="opacity-0 group-hover:opacity-100 h-7 w-7 sm:h-8 sm:w-8 text-red-500"
                            onClick={() => handleDeleteExpense(expense.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Settlements */}
      {summary?.settlements && summary.settlements.length > 0 && (
        <Card className="mt-4 sm:mt-6">
          <CardHeader className="pb-2 sm:pb-4">
            <CardTitle className="text-base sm:text-lg flex items-center gap-2">
              <Calculator className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" /> Pagos para Equilibrar
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-2 sm:gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {summary.settlements.map((settlement, idx) => (
                <div key={idx} className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg text-sm">
                  <span className="font-medium text-slate-700 dark:text-slate-200 truncate max-w-[80px] sm:max-w-none">{settlement.from_name}</span>
                  <ArrowRight className="w-4 h-4 text-emerald-500 shrink-0" />
                  <span className="font-medium text-slate-700 dark:text-slate-200 truncate max-w-[80px] sm:max-w-none">{settlement.to_name}</span>
                  <span className="ml-auto font-bold text-emerald-600 whitespace-nowrap">
                    ${formatCurrency(settlement.amount)}
                  </span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Edit Expense Dialog */}
      <Dialog open={editExpenseOpen} onOpenChange={setEditExpenseOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Gasto</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditExpense} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-paid-by">¿Quién pagó? *</Label>
              <Select
                value={editExpenseForm.paid_by_member_id}
                onValueChange={(v) => setEditExpenseForm({...editExpenseForm, paid_by_member_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecciona quién pagó" />
                </SelectTrigger>
                <SelectContent>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      {m.name} {m.user_id === user?.id && "(tú)"}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-expense-amount">Monto *</Label>
              <CurrencyInput
                id="edit-expense-amount"
                currency={userCurrency}
                placeholder="0"
                value={editExpenseForm.amount}
                onChange={(e) => setEditExpenseForm({...editExpenseForm, amount: e.target.value})}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-expense-desc">Concepto *</Label>
              <Input
                id="edit-expense-desc"
                placeholder="Ej: Cervezas, Hospedaje, Almuerzo"
                value={editExpenseForm.description}
                onChange={(e) => setEditExpenseForm({...editExpenseForm, description: e.target.value})}
              />
            </div>
            <Button type="submit" className="w-full btn-primary" disabled={submitting}>
              {submitting ? "Guardando..." : "Guardar Cambios"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Group Dialog with OTP */}
      <Dialog open={deleteGroupOpen} onOpenChange={setDeleteGroupOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Eliminar Grupo
            </DialogTitle>
          </DialogHeader>
          
          {deleteOtpStep === "request" ? (
            <div className="space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                ¿Estás seguro de que quieres eliminar el grupo <strong>{groupDetails?.name}</strong>?
              </p>
              <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-700 dark:text-red-300">
                <p className="font-medium">⚠️ Esta acción no se puede deshacer</p>
                <p className="mt-1">Se eliminarán todos los gastos y miembros del grupo.</p>
              </div>
              <p className="text-slate-500 text-sm">
                Por seguridad, te enviaremos un código de verificación a tu WhatsApp.
              </p>
              <Button 
                onClick={requestDeleteOtp} 
                variant="destructive" 
                className="w-full"
                disabled={submitting}
              >
                {submitting ? "Enviando código..." : "Solicitar Código de Verificación"}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-slate-600 dark:text-slate-300">
                Ingresa el código de 6 dígitos que enviamos a tu WhatsApp para confirmar la eliminación del grupo <strong>{deleteGroupName}</strong>.
              </p>
              <div className="space-y-2">
                <Label htmlFor="delete-otp">Código de verificación</Label>
                <Input
                  id="delete-otp"
                  placeholder="000000"
                  value={deleteOtp}
                  onChange={(e) => setDeleteOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="text-center text-2xl tracking-widest font-mono"
                  maxLength={6}
                />
              </div>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setDeleteOtpStep("request")}
                >
                  Volver
                </Button>
                <Button 
                  onClick={confirmDeleteGroup} 
                  variant="destructive" 
                  className="flex-1"
                  disabled={submitting || deleteOtp.length !== 6}
                >
                  {submitting ? "Eliminando..." : "Eliminar Grupo"}
                </Button>
              </div>
              <p className="text-xs text-slate-500 text-center">
                El código expira en 5 minutos
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SharedExpensesPage;
